import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export type Strategy = 'cc' | 'csp';

interface StrategyContextType {
  strategy: Strategy;
  setStrategy: (s: Strategy) => void;
}

const StrategyContext = createContext<StrategyContextType>({
  strategy: 'cc',
  setStrategy: () => {},
});

export const useStrategy = () => useContext(StrategyContext);

export const StrategyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize: URL param > localStorage > default 'cc'
  const [strategy, setStrategyState] = useState<Strategy>(() => {
    const urlStrategy = searchParams.get('strategy');
    if (urlStrategy === 'cc' || urlStrategy === 'csp') return urlStrategy;
    const stored = localStorage.getItem('selectedStrategy');
    if (stored === 'cc' || stored === 'csp') return stored;
    return 'cc';
  });

  const setStrategy = useCallback((s: Strategy) => {
    setStrategyState(s);
    localStorage.setItem('selectedStrategy', s);
    // Update URL param
    const newParams = new URLSearchParams(searchParams);
    if (s === 'cc') {
      newParams.delete('strategy');
    } else {
      newParams.set('strategy', s);
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync from URL on navigation
  useEffect(() => {
    const urlStrategy = searchParams.get('strategy');
    if (urlStrategy === 'csp' && strategy !== 'csp') {
      setStrategyState('csp');
      localStorage.setItem('selectedStrategy', 'csp');
    }
  }, [searchParams, strategy]);

  return (
    <StrategyContext.Provider value={{ strategy, setStrategy }}>
      {children}
    </StrategyContext.Provider>
  );
};
